import React, { useMemo } from 'react';
import { Box, ListItem, ListItemAvatar, ListItemText, Typography, Badge, Divider } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { format } from 'date-fns';
import { IRoom } from '../../redux/messages/types';
import { createFullName, getJWtDetails } from '../../utils/AppUtils';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MicIcon from '@mui/icons-material/Mic';
import ImageIcon from '@mui/icons-material/Image';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

interface IRoomItemProps {
    room: IRoom;
    onClick: (roomId: string) => void;
    isActive?: boolean;
    showDivider?: boolean;
}

const RoomItem: React.FC<IRoomItemProps> = ({ room, onClick, isActive, showDivider }) => {
    const { themeStyle } = useThemeContext();
    const { user_id } = getJWtDetails();
    const isSender = room.sender && room.sender._id === user_id;

    const isTyping = room.isUserTyping === 'yes';

    const roomName = useMemo(() => {
        if (isSender && room.receiver?.length) {
            const recipients = room.receiver.filter(user => typeof user !== 'string' && user._id !== user_id);
            if (recipients.length === 0) return 'No recipient';
            if (recipients.length === 1) return createFullName(recipients[0]);
            return `${createFullName(recipients[0])} +${recipients.length - 1}`;
        } else if (room.sender && typeof room.sender !== 'string') {
            const others = room.receiver.filter(user => typeof user !== 'string' && user._id !== user_id);
            if (others.length > 0) return `${createFullName(room.sender)} +${others.length}`;
            return createFullName(room.sender);
        }
        return 'Conversation';
    }, [room, user_id, isSender]);

    const roomAvatars = useMemo(() => {
        if (isSender && room.receiver?.length) {
            return room.receiver.filter(user => typeof user !== 'string' && user._id !== user_id).slice(0, 3);
        } else if (room.sender && typeof room.sender !== 'string') {
            const otherReceivers = room.receiver
                .filter(user => typeof user !== 'string' && user._id !== user_id)
                .slice(0, 2);
            return [room.sender, ...otherReceivers];
        }
        return [];
    }, [room, user_id, isSender]);

    const totalParticipantsCount = useMemo(() => {
        let count = 0;
        if (room.receiver) {
            count += room.receiver.filter(user =>
                typeof user === 'string' ? user !== user_id : user._id !== user_id,
            ).length;
        }
        if (room.sender && typeof room.sender !== 'string' && room.sender._id !== user_id) {
            count += 1;
        }
        return count;
    }, [room, user_id]);

    const unreadCount = useMemo(() => {
        if (room.unread_message_count?.length) {
            return room.unread_message_count.find(c => c.user === user_id)?.count || 0;
        }
        return 0;
    }, [room.unread_message_count, user_id]);

    const lastMessagePreview = useMemo(() => {
        const lastMessage = room.messages?.[0];

        if (!lastMessage) return '';

        if (isTyping) return 'Typing...';

        if (lastMessage.message_content) return lastMessage.message_content;

        const messageType = lastMessage.message_type || 'chat';

        switch (messageType) {
            case 'document_message':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <InsertDriveFileIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Document</span>
                    </Box>
                );
            case 'sound_message':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MicIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Audio</span>
                    </Box>
                );
            case 'picture_message':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ImageIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Photo</span>
                    </Box>
                );
            case 'location_message':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOnIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Location</span>
                    </Box>
                );
            case 'video_message':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <VideocamIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Video</span>
                    </Box>
                );
            case 'audition_invite':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EventAvailableIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: theme[themeStyle].highlight, fontSize: '14px' }}
                        />
                        <span>Audition Invitation</span>
                    </Box>
                );
            default:
                return lastMessage.message_content || '';
        }
    }, [room.messages, isTyping, themeStyle]);

    const lastMessageTime = useMemo(() => {
        try {
            const date = new Date(room.last_message_at);
            const today = new Date();
            const isToday = date.toDateString() === today.toDateString();
            return format(date, isToday ? 'h:mm a' : 'MM/dd/yy');
        } catch {
            return '';
        }
    }, [room.last_message_at]);

    return (
        <>
            <ListItem
                onClick={() => onClick(room._id)}
                sx={{
                    py: 2,
                    px: 2,
                    backgroundColor: isActive ? theme[themeStyle].deep_grey_lighter : 'transparent',
                    '&:hover': {
                        backgroundColor: theme[themeStyle].deep_grey,
                    },
                    cursor: 'pointer',
                }}>
                <ListItemAvatar>
                    <Box
                        sx={{
                            position: 'relative',
                            height: 50,
                            width: roomAvatars.length > 1 ? 66 : 50,
                        }}>
                        {roomAvatars.map((user, index) => (
                            <Box
                                key={user._id || index}
                                sx={{
                                    position: 'absolute',
                                    left: index * 16,
                                    zIndex: 3 - index,
                                    border: `1px solid ${theme[themeStyle].secondaryBg}`,
                                    borderRadius: '50%',
                                }}>
                                <ThspianAvatar user={user} sx={{ width: 50, height: 50 }} withMonogram />
                            </Box>
                        ))}

                        {totalParticipantsCount > 3 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: -5,
                                    bottom: -3,
                                    backgroundColor: theme[themeStyle].highlight,
                                    color: theme[themeStyle].true_white,
                                    borderRadius: '50%',
                                    width: 22,
                                    height: 22,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 11,
                                    fontWeight: 'bold',
                                    border: `1px solid ${theme[themeStyle].secondaryBg}`,
                                    zIndex: 4,
                                }}>
                                +{totalParticipantsCount - Math.min(3, roomAvatars.length)}
                            </Box>
                        )}
                    </Box>
                </ListItemAvatar>
                <ListItemText
                    disableTypography
                    primary={
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].tag_white,
                                    fontSize: 16,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '150px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}>
                                {roomName}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme[themeStyle].grey,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: 12,
                                }}>
                                {lastMessageTime}
                            </Typography>
                        </Box>
                    }
                    secondary={
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 0.5,
                            }}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].grey,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 12,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '200px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}>
                                {lastMessagePreview}
                            </Typography>
                            {unreadCount > 0 && (
                                <Badge
                                    badgeContent={unreadCount}
                                    color="error"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: theme[themeStyle].primary_red,
                                            color: theme[themeStyle].true_white,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 10,
                                        },
                                        position: 'absolute',
                                        left: 20,
                                        top: 30,
                                        zIndex: 9999,
                                    }}
                                />
                            )}
                        </Box>
                    }
                />
            </ListItem>
            {showDivider && <Divider sx={{ backgroundColor: theme[themeStyle].grey + '30' }} />}
        </>
    );
};

export default RoomItem;
